import { MassUnit } from '../converters';

export enum BodyScanGender {
    Female = 0,
    Male = 1,
}

export enum AnalysisValue {
    Target = 0,
    LowerLimit = 1,
    UpperLimit = 2,
    Current = 3,
}

export enum UserTargetTypes {
    Bodyweight = 'Bodyweight',
    Bodyfat = 'Bodyfat',
    Musclemass = 'Musclemass',
}

export enum BodyMetricGraphTypes {
    BodyweightHistory,
    BodyweightTargetAndHistory,
    BodyfatHistory,
    BodyfatTargetAndHistory,
    MusclemassHistory,
    MusclemassTargetAndHistory,
}

export interface IBodyMetricModel {
    graphType: BodyMetricGraphTypes;
    data: any;
    verticalGridLines: Date[];
    mileStones: any;
    minDate: Date;
    maxDate: Date;
    clippingPoint: Date | null;
    unit: string;
    ranges: any;
}

export enum BodyScanTypes {
    Tiger,
    Leaone,
    Manual,
}
export interface IWeightLog {
    id?: string;
    uid?: string;
    weight: number;
    testTime: string;
    invalid?: boolean;
    analysis: any;
    isManualWeightLog?: boolean;
}
export interface IBodyScanModel {
    date: Date;
    month: number;
    type: BodyScanTypes;
    bodyweight?: number;
    bodyfat?: number;
    musclemass?: number;
    target?: ITargetInfo;
    milestone?: ITargetInfo;
}

export interface IBodyScansAndLatest {
    scans: IBodyScanModel[];
    latest: IBodyScanModel;
}
export interface IWeightTargetInfo {
    target?: ITargetInfo;
    milestone?: ITargetInfo;
    currentWeight?: number;
}

export interface IMinMaxDefault {
    default: number;
    max: number;
    min: number;
    validRange: boolean;
    limitTarget: boolean;
}

export interface ITargetInfo {
    weight?: number;
    muscleMass?: number;
    bodyFat?: number;
    unit?: string;
    date: Date;
    shortDate: string;
    monthsNeeded: number;
    duration: string;
    dailyRateOfChange: number;
    weightChange?: number;
    muscleMassChange?: number;
    bodyFatChange?: number;
    targetType?: UserTargetTypes;
    reached?: boolean;
}

export interface ITargetInfoViewModel {
    date: Date;
    value?: number;
    reached: boolean;
    targetMonthsNeeded?: number;
}

export interface ITargetMetricValues {
    muscleMass: number;
    bodyFat: number;
    bodyWeight: number;
}
export interface IBodyScanData {
    id: string;
    email: string;
    dateOfScan: Date;
    dateOfScanDateOnly: Date;
    testTime: string;
    analysis: IAnalysis;
    weight?: number;
    calculatedBodyFat?: number;
    calculatedMuscleMassMeterKg?: number;
    invalid: boolean;
    type: BodyScanTypes;
    target?: ITargetInfo;
    milestone?: ITargetInfo;
    rawBodyFat?: number;
}
export interface IBodyScaleScanData {
    id?: string;
    uid: string;
    email: string;

    testTime: Date;
    invalid?: boolean;

    bodyTypeAssessment?: any;
    scanWeight?: number;
    calculatedMuscleMass?: number;
    calculatedBodyFat?: number;
    rawScan?: any;

    analysis?: any; // temp for backward compatibility (not IAnalysis)
}

export interface IBodyScanStat {
    date: Date;
    dateTime: string;
    year: number;
    month: number;
    'year-month': string;
    analysis: IAnalysis;
    type: BodyScanTypes;
    target?: ITargetInfo;
    milestone?: ITargetInfo;
}

export interface IBodyFatAdjustments {
    rawPreAdjustmentBodyFat: number;
    athleteAdjustment: number;
    bodyTypeAdjustment: number;
    safetyNetAdjustment: number;
    minimumBodyFatCorrectionAdjustment: number;
    adjustedBodyFat: number;
    proposedTrendLine: {
        x: number;
        y: number;
    };
}

export interface IScanStat {
    date: Date;
    month: number;
    bodyweight: number;
    bodyfat?: number;
}

export interface IBodyScanHistory extends IBodyScanData {
    weight: number;
    bodyFat: number;
    muscleMassMeter: number;
    unit: MassUnit;
    muscleMassMeterValue: number;
}

export interface IBodyScanAnalysisData {
    currentValue: number;
    lowerLimitValue: number;
    upperLimitValue: number;
}

export interface IAnalysis {
    score?: IBodyScanAnalysisData;
    percentBodyFat?: IBodyScanAnalysisData;
    bodyMassIndex?: IBodyScanAnalysisData;
    weight?: IBodyScanAnalysisData;
    waistHipRatio?: IBodyScanAnalysisData;
    skeletalMuscleMass?: IBodyScanAnalysisData;
    obesityDegree?: IBodyScanAnalysisData;
}

export enum ScanProgress {
    Onboarding = 'onboarding',
    OverrideBodyType = 'overrideBodyType',
    AssessmentResults = 'assessmentResults',
    BeforeScan = 'beforeScan',
    CaptureMetrics = 'capturemetrics',
    Notstarted = 'notstarted',
    Inprogress = 'inprogress',
    Complete = 'complete',
    RemoveScale = 'removeScale',
    Error = 'error',
}

export enum WeightTargetStep {
    BodyScanType = 'bodyScanType',
    BodyWeightReview = 'bodyWeightReview',
    BodyWeightAdjust = 'bodyWeightAdjust',
    BodyWeightSummary = 'bodyWeightSummary',
    BodyWeightSupport = 'bodyWeightSupport',
    BodyWeightPal = 'bodyWeightPal',
    BodyWeightScaleQuestion = 'bodyWeightScaleQuestion',
    Error = 'error',
}

export enum TargetStatus {
    MilestoneReached = 'milestoneReached',
    TargetReached = 'targetReached',
    TargetSet = 'targetSet',
    NotReached = 'notReached',
}

export type ScanStateMap = Record<string, { progress: ScanProgress, phase?: string }>;
export const progressUpdate: ScanStateMap = {
    INITIALISING: { progress: ScanProgress.Notstarted },
    SCANNING: { progress: ScanProgress.Notstarted, phase: 'scanning' },
    CONNECTING: { progress: ScanProgress.Inprogress, phase: 'connecting' },
    READING: { progress: ScanProgress.Inprogress, phase: 'reading' },
    CALCULATING: { progress: ScanProgress.Inprogress, phase: 'calculating' },
};
